import React from 'react';
import type { VideoUrl } from '../../../../types/banner/video-url';
import type { FormattedText } from '../../../../types/banner/formatted-text';
import type { DualSlotValue, GroupedImageSelectorValue } from '../../../../types/templates';

// Small display primitives for the asset-modal config view.
// One per FormField['type']; ConfigField switches on type and picks the right one.

const linkStyle: React.CSSProperties = { color: '#1890ff' };

export const StringDisplay: React.FC<{ value: string }> = ({ value }) => <span>{value}</span>;

export const NumberDisplay: React.FC<{ value: number }> = ({ value }) => <span>{String(value)}</span>;

export const BooleanDisplay: React.FC<{ value: boolean | 'si' | 'no' }> = ({ value }) => {
  const truthy = value === true || value === 'si';
  return <span>{truthy ? 'Sí' : 'No'}</span>;
};

export const FormattedTextDisplay: React.FC<{ value: FormattedText }> = ({ value }) => <span>{value.text}</span>;

export const VideoDisplay: React.FC<{ value: VideoUrl }> = ({ value }) => (
  <a href={value.originalUrl} target='_blank' rel='noopener noreferrer' style={linkStyle}>
    Link
  </a>
);

export const ImageDisplay: React.FC<{ value: string }> = ({ value }) => (
  <a href={value} target='_blank' rel='noopener noreferrer' style={linkStyle}>
    Link
  </a>
);

// Used for visual-search (DualSlotValue) — renders both slots side-by-side.
export const DualSlotDisplay: React.FC<{ value: DualSlotValue }> = ({ value }) => (
  <span>{[value.slot1, value.slot2].filter(Boolean).join(' · ')}</span>
);

export const TagListDisplay: React.FC<{ value: string[] }> = ({ value }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'flex-end' }}>
    {value.map((item, index) => (
      <span
        key={index}
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          backgroundColor: '#f1f5f9',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          fontSize: '0.85em',
          color: '#334155',
          whiteSpace: 'nowrap'
        }}
      >
        {item}
      </span>
    ))}
  </div>
);

export const GroupedSelectionDisplay: React.FC<{ value: GroupedImageSelectorValue }> = ({ value }) => (
  <TagListDisplay value={value.map(item => item.label ?? item.itemValue)} />
);

// Fallback when there's no FormField to drive the display (e.g. suggested_copy, viral_score, raw objects).
export const FallbackDisplay: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <span>{String(value)}</span>;
  }
  return <span>{JSON.stringify(value)}</span>;
};
